// Libraries
import React from 'react';
import {css} from 'aphrodite/no-important';
// Core
import CoreMetrics, {FormFactorChangeListener, sharedFormFactorChangeListener, createExpandableMediaQueryStylesheet} from '../core/Metrics';
import {TitleText, BodyText, CodeText, CaptionText} from '../core/Type';
import {FixedSpace} from '../core/Spacing';
// Layouts
import {Grid, GridRow, GridColumn, DockedTOCandContentLayout} from '../layouts/Grid';
import {CssGrid, CssGridRow, CssGridColumn, CssGridSpace} from '../layouts/CssGrid';
import MainAndSidebarLayout from '../layouts/MainAndSidebarLayout';
// Modules
import SiteBar from '../modules/SiteBar';
import LessonTOC from '../modules/LessonTOC';
import SiteFooter from '../modules/SiteFooter';
// Images
import scratchpadImage1 from './images/scratchpad-image-1.png';

export default class ScratchpadPage extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            page: {
                display: "flex",
                flexDirection: "column-reverse",
            },
            sidebar: {
                flexShrink: 0,
                marginRight: CoreMetrics.Grid.HorizontalGutter,
                width: CoreMetrics.Grid.Width.OneFourth,
                [CoreMetrics.MediaQuery().Desktop]: {
                    display: "block",
                },
                [CoreMetrics.MediaQuery().Tablet]: {
                    display: "none",
                },
                [CoreMetrics.MediaQuery().Phone]: {
                    display: "none",
                },
            },
            contentColumn: {
                display: "flex",
                flexDirection: "column",
            },
        });
        let page = null;
        const sidebarBehavior = sharedFormFactorChangeListener.getSidebarBehavior();
        if (sidebarBehavior === FormFactorChangeListener.SidebarBehavior.Docked) {
            page = (
                <div className={css(styles.page)}>
                    <DockedTOCandContentLayout toc={(
                        <LessonTOC variant={LessonTOC.Variant.Docked} />
                    )} content={(
                        <div className={css(styles.contentColumn)}>
                            <Grid>
                                <GridRow>
                                    <GridColumn variant={GridColumn.Variant.OneSixth}></GridColumn>
                                    <GridColumn>
                                        <ScratchpadHeader />
                                        <Scratchpad />
                                    </GridColumn>
                                    <GridColumn variant={GridColumn.Variant.OneSixth}></GridColumn>
                                </GridRow>
                            </Grid>
                            <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                            <SiteFooter />
                        </div>
                    )} />
                    <SiteBar variant={SiteBar.Variant.Stretched} />
                </div>
            );
        } else {
            const mainContent = <CssGrid sizeDesktop={9}>
                <CssGridRow>
                    <CssGridColumn sizeDesktop={9}>
                        <div className={css(styles.contentColumn)}>
                            <ScratchpadHeader />
                            <Scratchpad />
                        </div>
                    </CssGridColumn>
                </CssGridRow>
            </CssGrid>;

            page = (
                <div className={css(styles.page)}>
                    <SiteFooter />
                    <FixedSpace
                        variant={FixedSpace.Variant.Vertical}
                        size={FixedSpace.Size.XLarge}
                    />
                    <MainAndSidebarLayout
                        sidebarContent={<LessonTOC />}
                        mainContent={mainContent}
                    />
                    <SiteBar />
                </div>
            );
        }
        return page;
    }
}

class ScratchpadHeader extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            container: {
                paddingTop: CoreMetrics.ElementPadding.XLarge(),
            },
        });
        return (
            <div className={css(styles.container)}>
                <TitleText>Spin-off of "Project: Fish Tank"</TitleText>
            </div>
        );
    }
}

class Scratchpad extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            container: {
                paddingTop: CoreMetrics.ElementPadding.Medium(),
            },
            codeEditor: {
                whiteSpace: "pre-wrap",
                width: "calc(100% - 6px)",
                maxWidth: "calc(100% - 6px)",
                minHeight: 228,
                marginTop: 2,
            },
            image: {
                maxWidth: "100%",
            },
        });

        // HACK(diedra) This code is not indented to get the code on separate
        // lines in the code editor.
        // Hey this is a prototype, don't judge me!
        const initialCode = `
background(89, 216, 255);

var centerX = 200;
var centerY = 100;
var bodyLength = 118;
var bodyHeight = 74;
var bodyColor = color(162, 0, 255);

noStroke();
fill(bodyColor);
// body
ellipse(centerX, centerY, bodyLength, bodyHeight);
// tail
var tailWidth = bodyLength/4;
var tailHeight = bodyHeight/2;
triangle(centerX-bodyLength/2, centerY,
     centerX-bodyLength/2-tailWidth, centerY-tailHeight,
     centerX-bodyLength/2-tailWidth, centerY+tailHeight);
// eye
fill(33, 33, 33);
ellipse(centerX+bodyLength/4, centerY, bodyHeight/5, bodyHeight/5);
        `;

        const codeEditor = <CodeText variant={CodeText.Variant.Block}>
            <textarea className={css(styles.codeEditor)} defaultValue={initialCode}/>
        </CodeText>;

        const outputImage = <img
            className={css(styles.image)}
            src={scratchpadImage1}
            alt=""
        />;

        const sideSpace = <CssGridSpace size={1} sizePhone={0} />;

        return (
            <div className={css(styles.container)}>
                <FixedSpace variant={FixedSpace.Variant.Vertical} />

                <CssGrid sizeDesktop={9}>
                    <CssGridRow>
                        <CssGridColumn sizeTablet={5} sizeDesktop={6}>
                            {codeEditor}
                        </CssGridColumn>
                        <CssGridColumn size={3} sizePhone={0}>
                            {outputImage}
                        </CssGridColumn>
                    </CssGridRow>

                    <CssGridRow>
                        <CssGridColumn size={0} sizePhone={4}>
                            {outputImage}
                        </CssGridColumn>
                    </CssGridRow>

                    <CssGridRow>
                        {sideSpace}
                        <CssGridColumn sizeTablet={6} sizeDesktop={7}>
                            <FixedSpace variant={FixedSpace.Variant.Vertical} />
                            <BodyText>
                                This program draws a single fish. Poor lonely
                                fish! For this project, you'll use functions to
                                accompany him with more fish of all different
                                shapes and colors.
                            </BodyText>
                            <ol>
                                <li><BodyText>
                                    Create a custom function (like drawFish)
                                    that draws a fish at a given x and y with a
                                    given length, height, and color
                                    (5 parameters), using the starter code.
                                </BodyText></li>
                                <li><BodyText>
                                    Call that function to draw the fish.
                                </BodyText></li>
                                <li><BodyText>
                                    Now call that function lots more times,
                                    with different values, so your screen is
                                    filled with fish.
                                </BodyText></li>
                                <li><BodyText>
                                    Add more parameters to the function, like
                                    tail width, eye size, tail color - more
                                    ways that you can make each fish different
                                    from each other.
                                </BodyText></li>
                                <li><BodyText>
                                    Bonus: Add seaweed to the tank, and make a
                                    function to draw it at different places and
                                    heights. Or add pebbles to the bottom. Or a
                                    bubble drawing function.
                                </BodyText></li>
                                <li><BodyText>
                                    Bonus: Want to make it interactive? Use a
                                    mouseClicked function to add more fish
                                    wherever the user clicks.
                                </BodyText></li>
                            </ol>
                            <CaptionText>
                                Don't like fish? You could also make a pet shop,
                                maybe using your animal code from the
                                Design-an-Animal project, or a car shop, using
                                rectangles. The important thing is to use
                                functions to make your drawing code reusable.
                            </CaptionText>
                        </CssGridColumn>
                        {sideSpace}
                    </CssGridRow>
                </CssGrid>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/demo/ScratchpadPage.jsx